// 色の定数
const COLORS = {
  // プライマリー色
  primary: '#09529f',
  primaryDark: '#002348',
  
  // セカンダリー色
  secondary: '#6c757d',
  secondaryDark: '#545b62',
  
  // アクセント色
  info: '#17a2b8',
  infoDark: '#138496',
  
  // 状態色
  success: '#155724',
  successBg: '#d4edda',
  successBorder: '#c3e6cb',
  
  danger: '#dc3545',
  dangerDark: '#c82333',
  dangerText: '#721c24',
  dangerBg: '#f8d7da',
  dangerBorder: '#f5c6cb',
  
  warning: '#856404',
  warningBg: '#fff3cd',
  warningBorder: '#ffeaa7',
  
  // 認証状態色
  verified: '#155724',
  verifiedBg: '#d4edda',
  verifiedBorder: '#c3e6cb',
  
  unverified: '#856404',
  unverifiedBg: '#fff3cd',
  unverifiedBorder: '#ffeaa7',
  
  // グレー系
  gray100: '#f8f9fa',
  gray200: '#e9ecef',
  gray300: '#dee2e6',
  gray600: '#6c757d',
  gray800: '#495057',
  
  // テキスト色
  textPrimary: '#333',
  textSecondary: '#495057',
  textMuted: '#666',
  
  // 背景色
  white: '#ffffff',
  lightGray: '#f8f9fa',
};

// レイアウト定数
const LAYOUT = {
  maxWidth: '1000px',
  borderRadius: '8px',
  memberIdBorderRadius: '20px',
  verificationBorderRadius: '12px',
  
  spacing: {
    xs: '5px',
    sm: '10px',
    md: '12px',
    lg: '15px',
    xl: '20px',
    xxl: '25px',
    xxxl: '30px',
    huge: '40px',
  },
  
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '20px',
    xl: '24px',
    xxl: '32px',
  },
  
  fontWeight: {
    normal: 'normal',
    bold: 'bold',
    medium: '500',
  },
  
  minWidth: {
    button: '150px',
    label: '120px',
    manualButton: '250px',
  },
  
  gridMinWidth: '400px',
  manualGridMinWidth: '180px',
};

// ボーダー定数
const BORDERS = {
  light: `1px solid ${COLORS.gray300}`,
  primary: `3px solid ${COLORS.primary}`,
  primaryThick: `2px solid ${COLORS.primary}`,
  memberIdThick: `2px solid ${COLORS.memberIdBorder}`,
  bottom: `1px solid ${COLORS.gray200}`,
  manual: `1px solid ${COLORS.manualBorder}`,
  manualThick: `2px solid ${COLORS.manual}`,
};

/**
 * 基本レイアウトスタイル
 */
export const layoutStyles = {
  container: {
    padding: `${LAYOUT.spacing.huge} ${LAYOUT.spacing.xl}`,
    maxWidth: LAYOUT.maxWidth,
    margin: '0 auto'
  },

  header: {
    textAlign: 'center',
    marginBottom: LAYOUT.spacing.huge,
    paddingBottom: LAYOUT.spacing.xl,
    borderBottom: BORDERS.primary
  },

  title: {
    fontSize: LAYOUT.fontSize.xxl,
    color: COLORS.primary,
    marginBottom: LAYOUT.spacing.sm,
    fontWeight: LAYOUT.fontWeight.bold
  },

  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: LAYOUT.spacing.xxxl,
    marginBottom: LAYOUT.spacing.huge,
    width: '100%'
  },
};

/**
 * メッセージスタイル
 */
export const messageStyles = {
  loadingMessage: {
    textAlign: 'center',
    padding: LAYOUT.spacing.huge,
    fontSize: LAYOUT.fontSize.base,
    color: COLORS.textMuted
  },

  errorMessage: {
    textAlign: 'center',
    padding: LAYOUT.spacing.xl,
    backgroundColor: COLORS.dangerBg,
    color: COLORS.dangerText,
    border: `1px solid ${COLORS.dangerBorder}`,
    borderRadius: LAYOUT.spacing.xs,
    marginBottom: LAYOUT.spacing.xl
  },

  successMessage: {
    backgroundColor: COLORS.successBg,
    color: COLORS.success,
    padding: LAYOUT.spacing.lg,
    borderRadius: LAYOUT.spacing.xs,
    margin: `${LAYOUT.spacing.xl} 0`,
    border: `1px solid ${COLORS.successBorder}`,
    textAlign: 'center'
  },
};

/**
 * カードスタイル
 */
export const cardStyles = {
  card: {
    backgroundColor: COLORS.lightGray,
    border: BORDERS.light,
    borderRadius: LAYOUT.borderRadius,
    padding: LAYOUT.spacing.xxl,
  },

  cardTitle: {
    fontSize: LAYOUT.fontSize.lg,
    fontWeight: LAYOUT.fontWeight.bold,
    color: COLORS.textPrimary,
    marginBottom: LAYOUT.spacing.xl,
    textAlign: 'center',
    paddingBottom: LAYOUT.spacing.sm,
    borderBottom: BORDERS.primaryThick
  },
};

/**
 * 情報項目スタイル
 */
export const infoStyles = {
  infoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: `${LAYOUT.spacing.md} 0`,
    borderBottom: BORDERS.bottom,
    gap: LAYOUT.spacing.sm,
    flexWrap: 'wrap'
  },

  infoLabel: {
    fontWeight: LAYOUT.fontWeight.bold,
    color: COLORS.textSecondary,
    fontSize: LAYOUT.fontSize.sm,
    minWidth: LAYOUT.minWidth.label,
    flexShrink: 0,
    textAlign: 'left'
  },

  infoValue: {
    color: COLORS.textPrimary,
    fontSize: LAYOUT.fontSize.sm,
    textAlign: 'right',
    wordBreak: 'break-word',
    maxWidth: '100%',
    flex: 1
  },
};

/**
 * バッジスタイル
 */
export const badgeStyles = {
  verificationBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.xs,
    padding: `4px ${LAYOUT.spacing.xs}`,
    borderRadius: LAYOUT.verificationBorderRadius,
    fontSize: LAYOUT.fontSize.xs,
    fontWeight: LAYOUT.fontWeight.bold
  },

  verifiedBadge: {
    backgroundColor: COLORS.verifiedBg,
    color: COLORS.verified,
    border: `1px solid ${COLORS.verifiedBorder}`
  },

  unverifiedBadge: {
    backgroundColor: COLORS.unverifiedBg,
    color: COLORS.unverified,
    border: `1px solid ${COLORS.unverifiedBorder}`
  },
};

/**
 * ボタンスタイル
 */
export const buttonStyles = {
  actionButtons: {
    display: 'flex',
    gap: LAYOUT.spacing.lg,
    justifyContent: 'center',
    flexWrap: 'wrap'
  },

  button: {
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.xl}`,
    border: 'none',
    borderRadius: '6px',
    fontSize: LAYOUT.fontSize.base,
    fontWeight: LAYOUT.fontWeight.bold,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
    display: 'inline-block',
    textAlign: 'center',
    minWidth: LAYOUT.minWidth.button
  },

  primaryButton: {
    backgroundColor: COLORS.primary,
    color: COLORS.white
  },

  secondaryButton: {
    backgroundColor: COLORS.secondary,
    color: COLORS.white
  },

  dangerButton: {
    backgroundColor: COLORS.danger,
    color: COLORS.white
  },
};

/**
 * アコーディオンスタイル
 */
export const accordionStyles = {
  accordionSection: {
    marginBottom: LAYOUT.spacing.lg,
    border: BORDERS.light,
    borderRadius: LAYOUT.borderRadius,
    backgroundColor: COLORS.white,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
  },

  accordionHeader: {
    width: '100%',
    padding: LAYOUT.spacing.lg,
    backgroundColor: COLORS.white,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    transition: 'background-color 0.15s ease',
    fontSize: LAYOUT.fontSize.base,
    fontWeight: LAYOUT.fontWeight.bold,
    color: COLORS.textPrimary,
  },

  accordionHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    flex: 1
  },

  accordionTitle: {
    textAlign: 'left',
    fontSize: LAYOUT.fontSize.base,
    fontWeight: LAYOUT.fontWeight.bold
  },

  accordionIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.primary
  },

  accordionArrow: {
    color: COLORS.textMuted,
    transition: 'transform 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  accordionContent: {
    backgroundColor: COLORS.white,
    borderTop: BORDERS.light
  },

  accordionButton: {
    width: '100%',
    padding: LAYOUT.spacing.lg,
    backgroundColor: COLORS.white,
    border: 'none',
    borderBottom: BORDERS.bottom,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    transition: 'background-color 0.15s ease',
    fontSize: LAYOUT.fontSize.sm,
    color: COLORS.textPrimary,
    textAlign: 'left',
  },

  accordionButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    backgroundColor: COLORS.gray100,
    color: COLORS.gray600,
  },

  accordionButtonText: {
    flex: 1,
    fontSize: LAYOUT.fontSize.sm
  },

  accordionButtonArrow: {
    color: COLORS.textMuted,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

/**
 * マニュアルセクションスタイル
 */
export const manualStyles = {
  manualSection: {
    marginTop: LAYOUT.spacing.huge,
    padding: LAYOUT.spacing.xxl,
    backgroundColor: COLORS.white,
    border: BORDERS.light,
    borderRadius: LAYOUT.borderRadius,
    whiteSpace: 'nowrap',
    textAlign: 'center'
  },

  manualTitle: {
    fontSize: LAYOUT.fontSize.xl,
    fontWeight: LAYOUT.fontWeight.bold,
    color: COLORS.primary,
    marginBottom: LAYOUT.spacing.lg,
    textAlign: 'center',
    paddingBottom: LAYOUT.spacing.sm,
    borderBottom: BORDERS.manualThick,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: LAYOUT.spacing.sm
  },

  manualDescription: {
    fontSize: LAYOUT.fontSize.base,
    color: COLORS.textMuted,
    marginBottom: LAYOUT.spacing.xl,
    lineHeight: '1.6',
    textAlign: 'center'
  },

  manualGrid: {
    display: 'flex',
    gridTemplateColumns: `repeat(auto-fit, minmax(${LAYOUT.manualGridMinWidth}, 1fr))`,
    gap: LAYOUT.spacing.lg,
    justifyContent: 'center',
    flexWrap: 'wrap',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      gridTemplateColumns: '1fr',
      gap: LAYOUT.spacing.sm
    }
  }
};

/**
 * サービス情報スタイル
 */
export const serviceStyles = {
  serviceInfo: {
    marginTop: LAYOUT.spacing.huge,
    padding: LAYOUT.spacing.xxl,
    backgroundColor: COLORS.white,
    border: BORDERS.light,
    borderRadius: LAYOUT.borderRadius,
    textAlign: 'center'
  },

  serviceTitle: {
    fontSize: LAYOUT.fontSize.xl,
    color: COLORS.primary,
    marginBottom: LAYOUT.spacing.lg,
    fontWeight: LAYOUT.fontWeight.bold
  },

  serviceDescription: {
    fontSize: LAYOUT.fontSize.base,
    color: COLORS.textMuted,
    lineHeight: '1.6',
    marginBottom: LAYOUT.spacing.xl
  }
};

/**
 * デバッグ情報スタイル
 */
export const debugStyles = {
  debugContainer: {
    marginTop: LAYOUT.spacing.huge,
    padding: LAYOUT.spacing.lg,
    backgroundColor: COLORS.lightGray,
    border: BORDERS.light,
    borderRadius: LAYOUT.spacing.xs,
    fontSize: LAYOUT.fontSize.xs,
    fontFamily: 'monospace'
  },

  debugSummary: {
    cursor: 'pointer',
    fontWeight: LAYOUT.fontWeight.bold
  },

  debugPre: {
    marginTop: LAYOUT.spacing.sm,
    whiteSpace: 'pre-wrap'
  }
};

/**
 * ホバー効果のスタイル生成関数
 */
export const hoverStyles = {
  // プライマリーボタンのホバー効果
  primaryButtonHover: {
    backgroundColor: COLORS.primaryDark,
    transform: 'translateY(-1px)'
  },

  // セカンダリーボタンのホバー効果
  secondaryButtonHover: {
    backgroundColor: COLORS.secondaryDark,
    transform: 'translateY(-1px)'
  },

  // 危険ボタンのホバー効果
  dangerButtonHover: {
    backgroundColor: COLORS.dangerDark,
    transform: 'translateY(-1px)'
  },

  // マニュアルボタンのホバー効果
  manualButtonHover: {
    backgroundColor: COLORS.secondary,
    borderColor: COLORS.secondaryDark,
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },

  // ホバー解除時のスタイル
  buttonHoverLeave: {
    transform: 'translateY(0)'
  }
};

/**
 * 全スタイルを統合したオブジェクト（従来の方式との互換性のため）
 */
export const homePageStyles = {
  ...layoutStyles,
  ...messageStyles,
  ...cardStyles,
  ...infoStyles,
  ...badgeStyles,
  ...buttonStyles,
  ...accordionStyles,
  ...manualStyles,
  ...serviceStyles,
  ...debugStyles,
};

/**
 * スタイル適用のユーティリティ関数
 */
export const styleUtils = {
  // 認証バッジのスタイル取得
  getVerificationBadgeStyle: (isVerified) => ({
    ...badgeStyles.verificationBadge,
    ...(isVerified ? badgeStyles.verifiedBadge : badgeStyles.unverifiedBadge)
  }),

  // ボタンのホバースタイル取得
  getButtonHoverStyle: (baseStyle, variant) => {
    const hoverVariants = {
      primary: hoverStyles.primaryButtonHover,
      secondary: hoverStyles.secondaryButtonHover,
      danger: hoverStyles.dangerButtonHover,
      manual: hoverStyles.manualButtonHover,
    };

    return {
      ...baseStyle,
      ...hoverVariants[variant]
    };
  },

  // ボタンのマウスリーブスタイル取得
  getButtonLeaveStyle: (baseStyle, variant) => {
    const leaveVariants = {
      primary: buttonStyles.primaryButton,
      secondary: buttonStyles.secondaryButton,
      danger: buttonStyles.dangerButton,
      manual: manualStyles.manualButton,
    };

    return {
      ...baseStyle,
      ...leaveVariants[variant],
      ...hoverStyles.buttonHoverLeave
    };
  },

  // デバッグ情報の表示判定
  shouldShowDebug: () => process.env.NODE_ENV === 'development'
};

/**
 * テーマ設定（将来の拡張用）
 */
export const themes = {
  default: {
    colors: COLORS,
    layout: LAYOUT,
  }
  // 他のテーマを追加可能
};

// デフォルトエクスポート
export default homePageStyles;